import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { candidateLogin, externalLogin, type CandidateLoginResult, type ExternalLoginResult, type ExternalPortalUser } from '@/lib/api';
import { candidateUserFromLoginResult } from '@/lib/candidate-user-from-auth';
import { registerUnauthorizedHandler, setCandidateSession, clearCandidateSession, getCandidateAccessToken, getCandidateRefreshToken, CANDIDATE_USER_KEY } from '@/lib/session';
import { API_BASE, tryRefreshSession } from '@/lib/api-client';
import type { CandidateUser } from '@/types/database';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AuthState {
  user: CandidateUser | null;
  staffId: string | null;
  eligibleGrades: string[];
  isExternalCandidate: boolean;
  applicantRefNo: string | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  signIn: (staffId: string, password: string) => Promise<void>;
  signInExternal: (email: string, password: string, otp?: string) => Promise<void>;
  /** Hydrate auth state from an already-fetched login result (dev impersonate, OAuth). */
  loginWithResult: (result: CandidateLoginResult) => void;
  /** Hydrate auth state from an external login result (post-setup auto-login). */
  loginWithExternalResult: (result: ExternalLoginResult) => void;
  signOut: () => void;
  markUndertakingAccepted: (acceptedAt: string) => void;
}

// ── Session timeout constants ─────────────────────────────────────────────────

const SESSION_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours
const WARNING_BEFORE = 5 * 60 * 1000; // warn 5 min before
const PROACTIVE_REFRESH_INTERVAL = 50 * 60 * 1000; // refresh before 60m JWT expiry

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ── Session helpers ───────────────────────────────────────────────────────────

function saveUserProfile(user: CandidateUser) {
  localStorage.setItem(CANDIDATE_USER_KEY, JSON.stringify(user));
}

function loadSavedUser(): CandidateUser | null {
  try {
    const raw = localStorage.getItem(CANDIDATE_USER_KEY);
    return raw ? (JSON.parse(raw) as CandidateUser) : null;
  } catch {
    return null;
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    staffId: null,
    eligibleGrades: [],
    isExternalCandidate: false,
    applicantRefNo: null,
    loading: true,
    error: null,
  });
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutSecondsLeft, setTimeoutSecondsLeft] = useState(WARNING_BEFORE / 1000);
  const [refreshingSession, setRefreshingSession] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const warningRef = useRef<ReturnType<typeof setTimeout>>();
  const countdownRef = useRef<ReturnType<typeof setInterval>>();
  const proactiveRefreshRef = useRef<ReturnType<typeof setTimeout>>();
  const isLoggedInRef = useRef(false);

  // ── Inactivity timers ────────────────────────────────────────────────────

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (proactiveRefreshRef.current) clearTimeout(proactiveRefreshRef.current);
  }, []);

  const signOut = useCallback((reason: 'manual' | 'expired' = 'manual') => {
    clearTimers();
    setShowTimeoutWarning(false);
    setTimeoutSecondsLeft(WARNING_BEFORE / 1000);
    isLoggedInRef.current = false;
    const token = getCandidateAccessToken();
    void fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
    clearCandidateSession();
    setState({
      user: null,
      staffId: null,
      eligibleGrades: [],
      isExternalCandidate: false,
      applicantRefNo: null,
      loading: false,
      error: reason === 'expired' ? 'Your session expired. Please sign in again.' : null,
    });
  }, [clearTimers]);

  useEffect(() => {
    registerUnauthorizedHandler(() => signOut('expired'));
    return () => registerUnauthorizedHandler(null);
  }, [signOut]);

  const resetTimers = useCallback(() => {
    if (!isLoggedInRef.current) return;
    setShowTimeoutWarning(false);
    setTimeoutSecondsLeft(WARNING_BEFORE / 1000);
    clearTimers();
    warningRef.current = setTimeout(
      () => {
        setShowTimeoutWarning(true);
        setTimeoutSecondsLeft(WARNING_BEFORE / 1000);
        countdownRef.current = setInterval(() => {
          setTimeoutSecondsLeft((seconds) => Math.max(0, seconds - 1));
        }, 1000);
      },
      SESSION_TIMEOUT - WARNING_BEFORE,
    );
    timeoutRef.current = setTimeout(() => signOut('expired'), SESSION_TIMEOUT);
    proactiveRefreshRef.current = setTimeout(() => {
      void tryRefreshSession().then((ok) => {
        if (ok && isLoggedInRef.current) resetTimers();
      });
    }, PROACTIVE_REFRESH_INTERVAL);
  }, [clearTimers, signOut]);

  const continueSession = useCallback(async () => {
    setRefreshingSession(true);
    try {
      const refreshed = await tryRefreshSession();
      if (!refreshed) {
        signOut('expired');
        return;
      }
      resetTimers();
    } finally {
      setRefreshingSession(false);
    }
  }, [resetTimers, signOut]);

  useEffect(() => {
    const events: Array<keyof DocumentEventMap> = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
    ];
    events.forEach((e) => document.addEventListener(e, resetTimers));
    return () =>
      events.forEach((e) => document.removeEventListener(e, resetTimers));
  }, [resetTimers]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !isLoggedInRef.current) return;
      void tryRefreshSession().then((ok) => {
        if (ok && isLoggedInRef.current) resetTimers();
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [resetTimers]);

  // ── Restore session on mount via silent refresh ──────────────────────────
  // Refresh token lives in httpOnly cookie. Show cached user profile instantly
  // while the silent refresh is in-flight to avoid a blank screen.

  useEffect(() => {
    const saved = loadSavedUser();
    if (saved) {
      const isExternal = !saved.staffId && !!saved.applicantRefNo;
      setState({
        user: saved,
        staffId: saved.staffId ?? null,
        eligibleGrades: saved.eligibleGrades ?? [],
        isExternalCandidate: isExternal,
        applicantRefNo: saved.applicantRefNo ?? null,
        loading: true,
        error: null,
      });
    }

    // Try silent refresh to get a fresh access token.
    const refreshToken = getCandidateRefreshToken();
    fetch(`${API_BASE}/api/auth/refresh/candidate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: refreshToken ? JSON.stringify({ refreshToken }) : undefined,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('refresh failed');
        const data = (await res.json()) as { accessToken: string; refreshToken?: string };
        setCandidateSession({ accessToken: data.accessToken, refreshToken: data.refreshToken });
        if (saved) {
          isLoggedInRef.current = true;
          setState((prev) => ({ ...prev, loading: false }));
          resetTimers();
        } else {
          setState({ user: null, staffId: null, eligibleGrades: [], isExternalCandidate: false, applicantRefNo: null, loading: false, error: null });
        }
      })
      .catch(() => {
        clearCandidateSession();
        setState({ user: null, staffId: null, eligibleGrades: [], isExternalCandidate: false, applicantRefNo: null, loading: false, error: null });
      });

    return () => clearTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sign in ──────────────────────────────────────────────────────────────

  const signIn = async (staffId: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await candidateLogin(staffId, password);
      const user = candidateUserFromLoginResult(result);

      setCandidateSession({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      saveUserProfile(user);
      isLoggedInRef.current = true;
      resetTimers();

      setState({
        user,
        staffId: result.staffId ?? null,
        eligibleGrades: result.eligibleGrades,
        isExternalCandidate: false,
        applicantRefNo: null,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw err;
    }
  };

  const signInExternal = async (email: string, password: string, otp?: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await externalLogin(email, password, otp);
      const u = result.user;
      const user: CandidateUser = {
        id: u.id,
        guid: u.guid,
        staffId: null,
        applicantRefNo: u.applicantRefNo,
        emailAddress: u.emailAddress,
        surname: u.surname,
        firstName: u.firstName,
        middleName: u.middleName ?? null,
        gender: u.gender,
        dateOfBirth: null,
        grade: null,
        designation: null,
        dateOfEmployment: null,
        nrd: null,
        yearsOfService: null,
        dateOfLastPromotion: null,
        phoneExt: null,
        gsmNumber: u.gsmNumber,
        officeLabel: null,
        departmentCode: null,
        department: null,
        divisionCode: null,
        division: null,
        directorateCode: null,
        directorate: null,
        locationCode: null,
        location: null,
        skillPoolId: null,
        performanceTracking: null,
        verifiedQualificationWithDate: null,
        rank: null,
        eligibleForM3: null,
        eligibleForM4: null,
        eligibleForM5: null,
        eligibleForM6: null,
        m3EligibilityReason: null,
        m4EligibilityReason: null,
        m5EligibilityReason: null,
        m6EligibilityReason: null,
        eligibleGrades: [],
        undertakingAcceptedAt: u.undertakingAcceptedAt,
        isActive: u.isActive,
        lastLoginAt: u.lastLoginAt,
        createdAt: u.createdAt,
      };

      setCandidateSession({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      saveUserProfile(user);
      isLoggedInRef.current = true;
      resetTimers();

      setState({
        user,
        staffId: null,
        eligibleGrades: [],
        isExternalCandidate: true,
        applicantRefNo: result.applicantRefNo,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw err;
    }
  };

  const loginWithResult = useCallback((result: CandidateLoginResult) => {
    const user = candidateUserFromLoginResult(result);
    setCandidateSession({ accessToken: result.accessToken, refreshToken: result.refreshToken });
    saveUserProfile(user);
    isLoggedInRef.current = true;
    resetTimers();
    setState({
      user,
      staffId: result.staffId ?? null,
      eligibleGrades: result.eligibleGrades,
      isExternalCandidate: false,
      applicantRefNo: null,
      loading: false,
      error: null,
    });
  }, [resetTimers]);

  const loginWithExternalResult = useCallback((result: ExternalLoginResult) => {
    const u: ExternalPortalUser = result.user;
    const user: CandidateUser = {
      id: u.id,
      guid: u.guid,
      staffId: null,
      applicantRefNo: u.applicantRefNo,
      emailAddress: u.emailAddress,
      surname: u.surname,
      firstName: u.firstName,
      middleName: u.middleName ?? null,
      gender: u.gender,
      dateOfBirth: null,
      grade: null,
      designation: null,
      dateOfEmployment: null,
      nrd: null,
      yearsOfService: null,
      dateOfLastPromotion: null,
      phoneExt: null,
      gsmNumber: u.gsmNumber,
      officeLabel: null,
      departmentCode: null,
      department: null,
      divisionCode: null,
      division: null,
      directorateCode: null,
      directorate: null,
      locationCode: null,
      location: null,
      skillPoolId: null,
      performanceTracking: null,
      verifiedQualificationWithDate: null,
      rank: null,
      eligibleForM3: null,
      eligibleForM4: null,
      eligibleForM5: null,
      eligibleForM6: null,
      m3EligibilityReason: null,
      m4EligibilityReason: null,
      m5EligibilityReason: null,
      m6EligibilityReason: null,
      eligibleGrades: [],
      undertakingAcceptedAt: u.undertakingAcceptedAt,
      isActive: u.isActive,
      lastLoginAt: u.lastLoginAt,
      createdAt: u.createdAt,
    };
    setCandidateSession({ accessToken: result.accessToken, refreshToken: result.refreshToken });
    saveUserProfile(user);
    isLoggedInRef.current = true;
    resetTimers();
    setState({
      user,
      staffId: null,
      eligibleGrades: [],
      isExternalCandidate: true,
      applicantRefNo: result.applicantRefNo,
      loading: false,
      error: null,
    });
  }, [resetTimers]);

  const markUndertakingAccepted = useCallback((acceptedAt: string) => {
    setState((prev) => {
      if (!prev.user) return prev;
      const user: CandidateUser = { ...prev.user, undertakingAcceptedAt: acceptedAt };
      saveUserProfile(user);
      return { ...prev, user };
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...state, signIn, signInExternal, loginWithResult, loginWithExternalResult, signOut, markUndertakingAccepted }}
    >
      {children}
      {showTimeoutWarning && state.user && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground/60 backdrop-blur-sm">
          <div className="bg-card rounded-lg p-6 shadow-xl max-w-md mx-4 animate-scale-in">
            <h3 className="text-lg font-semibold mb-2">Session Expiring</h3>
            <p className="text-muted-foreground mb-4">
              Your session will expire in {Math.floor(timeoutSecondsLeft / 60)}:
              {String(timeoutSecondsLeft % 60).padStart(2, '0')} due to inactivity.
              Any unsaved changes on this device will be kept where possible.
            </p>
            <button
              type="button"
              onClick={() => void continueSession()}
              disabled={refreshingSession}
              className="w-full gradient-gold text-accent-foreground font-semibold py-2 rounded-md"
            >
              {refreshingSession ? 'Continuing...' : 'Continue Session'}
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

const defaultCtx: AuthContextType = {
  user: null,
  staffId: null,
  eligibleGrades: [],
  isExternalCandidate: false,
  applicantRefNo: null,
  loading: true,
  error: null,
  signIn: async () => {},
  signInExternal: async () => {},
  loginWithResult: () => {},
  loginWithExternalResult: () => {},
  signOut: () => {},
  markUndertakingAccepted: () => {},
};

export function useAuth() {
  return useContext(AuthContext) ?? defaultCtx;
}
